import { createTRPCClient, httpLink } from '@trpc/client'
import { createTRPCContext } from '@trpc/tanstack-react-query'
import { QueryClient } from '@tanstack/react-query'
import type { AppRouter } from '@joker/api/router'

/**
 * Same origin in production (the API serves this bundle) and proxied in
 * development, so there is never a base URL to configure and never a CORS
 * rule to maintain.
 *
 * One request per query, not a batch. The person page fires ten queries at
 * once and every panel is meant to load on its own; a batch ties them back
 * together, so one that fails (or one URL Fastify refuses as too long) takes
 * its neighbours down with it. Ten small same-origin requests cost nothing
 * that matters here.
 */
export const trpcClient = createTRPCClient<AppRouter>({
  links: [httpLink({ url: '/trpc' })],
})

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, err) => {
        const e = String(err)
        // Never retry an auth failure: it will not start working.
        if (e.includes('UNAUTHORIZED') || e.includes('FORBIDDEN')) return false
        /* A restart or a deploy answers with nginx's error page for a few
           seconds, which reads as "Unable to transform response from server".
           That kind of failure is worth waiting out — about half a minute —
           rather than showing "That did not load" for a panel that loads fine
           a moment later. A real error still shows after two tries. */
        const passing = /transform response|Failed to fetch|NetworkError|Load failed|50[234]/.test(e)
        return count < (passing ? 6 : 2)
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
  },
})

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>()
